import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        console.error("Payment status check failed, retrying:", e);
      }
      if (attempts >= 20) {
        setState("failed");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line

  if (state === "polling") {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-32 text-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-700" />
        <h1 className="mt-6 font-display text-2xl font-bold text-stone-900">Confirming your payment…</h1>
        <p className="mt-2 text-stone-600">Please don't close this window.</p>
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="mx-auto max-w-xl px-6 py-32 text-center">
        <h1 className="font-display text-3xl font-bold text-stone-900">We couldn't confirm your payment</h1>
        <p className="mt-3 text-stone-600">If you were charged, please contact us with your details. Otherwise, try again.</p>
        <Link to="/menu" className="mt-6 inline-block rounded-full bg-orange-700 px-6 py-3 font-bold text-white hover:bg-orange-800">Back to Menu</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-6 py-16">
      <div className="rounded-3xl border border-emerald-200 bg-white p-8 text-center shadow-sm" data-testid="order-confirmation">
        <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" />
        <h1 className="mt-4 font-display text-3xl font-extrabold text-stone-900">Order Confirmed!</h1>
        {order && (
          <>
            <p className="mt-2 text-stone-600">Order <span className="font-bold text-orange-700" data-testid="order-number">{order.order_number}</span></p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-900">
              <Clock className="h-4 w-4" /> Pickup: {order.pickup_time_display}
            </div>

            <div className="mt-6 space-y-2 text-left">
              {order.items.map((it, idx) => (
                <div key={`${it.name}-${idx}`} className="flex justify-between border-b border-stone-100 pb-2 text-sm">
                  <div>
                    <span className="font-semibold text-stone-900">{it.quantity}× {it.name}</span>
                    {it.selected_options?.length > 0 && (
                      <div className="text-xs text-stone-500">{it.selected_options.map((o) => o.label).join(", ")}</div>
                    )}
                  </div>
                  <span className="font-semibold">{money(it.line_total)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 text-lg font-bold text-stone-900"><span>Total</span><span>{money(order.total)}</span></div>
            </div>

            <p className="mt-6 text-sm text-stone-500">A confirmation email has been sent to {order.customer?.email}. See you soon!</p>
          </>
        )}
        <Link to="/menu" className="mt-6 inline-block rounded-full bg-orange-700 px-6 py-3 font-bold text-white hover:bg-orange-800">Order Again</Link>
      </div>
    </div>
  );
}
